import { CrmFieldService, StandardIds, SystemFieldIds } from '@galaxy/crm/services/field-service';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  SecurityContext,
  signal,
  Signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { ActivityInterface } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslationModule } from '../../../i18n/translation-module';
import { ObjectType } from '../../../tokens-and-interfaces';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { ActivityExtended } from '../timeline/timeline.service';
import { DomSanitizer } from '@angular/platform-browser';
import { CrmActivityAssociationPopoverComponent } from './association-popover.component';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { convertMillisecondsToHoursAndMinutes, MeetingSummaryModalComponent } from './meeting-summary-modal.component';
import { ActivityAssociationDisplayInfo, ActivityAssociationService } from './activity-association.service';
import { ActivityAssigneeService } from '../../../shared-services';
import { map } from 'rxjs/operators';
import { MeetingStatus } from './activity.interface';

export interface ActivityCardInformation {
  type: 'CardInfo';
  meetingBody: string;
  meetingStatus: string;
  meetingLocation: string;
  meetingDuration: string;
  date: Date | null;
  duration: number;
  meetingSummaryTitle: string;
  meetingSummaryBody: string;
  meetingSummarySentiment: number;
  meetingSalesScore: number;
  meetingKeyTakeAways: string[];
  meetingActionItems: string[];
  meetingTranscriptUrl: string;
  meetingRecordingUrl: string;
  processedTranscriptID: string;
  ownerId: string;
  activity: ActivityInterface;
}

@Component({
  selector: 'crm-activity-meeting',
  templateUrl: './meeting.component.html',
  styleUrls: ['./meeting.component.scss'],
  providers: [RxState],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    CollapsibleSectionComponent,
    MatCardModule,
    GalaxyAvatarModule,
    GalaxyPipesModule,
    CrmActivityAssociationPopoverComponent,
    MatIcon,
  ],
})
export class CrmActivityMeetingComponent {
  dateFormat = DateFormat.mediumDateShortTime;
  private _cardInfo: WritableSignal<Partial<ActivityCardInformation>> = signal({});
  private _associatedObjectId: WritableSignal<string> = signal('');
  private _objectType: WritableSignal<ObjectType> = signal('Contact');
  companies: Signal<ActivityAssociationDisplayInfo[]> = this.associationService.companies;
  private isClosed = false;

  @Input() set objectType(objectType: ObjectType) {
    this._objectType.set(objectType);
  }

  get objectType() {
    return this._objectType();
  }

  @Input() set associatedObjectId(crmObjectId: string) {
    this._associatedObjectId.set(crmObjectId);
  }

  get associatedObjectId() {
    return this._associatedObjectId();
  }

  private _showAvatar = true;
  @Input() set showAvatar(showAvatar: boolean) {
    this._showAvatar = showAvatar;
  }

  get showAvatar() {
    return this._showAvatar;
  }

  @Input({ required: true }) set cardInfo(data: ActivityExtended | Partial<ActivityCardInformation>) {
    if (this.checkIfActivityCardInformation(data)) {
      this._cardInfo.set(data as ActivityCardInformation);
    } else {
      this.associationService.activity = data as ActivityExtended;
      this._cardInfo.set(this.convertActivityToCardInfo(data as ActivityExtended));
    }
  }

  get cardInfo(): Partial<ActivityCardInformation> {
    return this._cardInfo();
  }

  private checkIfActivityCardInformation(data: any): data is ActivityCardInformation {
    return 'type' in data && data.type === 'CardInfo';
  }

  private convertActivityToCardInfo(data: ActivityExtended): Partial<ActivityCardInformation> {
    const duration = this.getIntegerFieldData(data, StandardIds.ActivityMeetingDuration);
    return {
      meetingBody:
        this.sanitizer.sanitize(SecurityContext.HTML, this.getStringFieldData(data, StandardIds.ActivityMeetingBody)) ||
        '',
      meetingStatus: this.getStringFieldData(data, StandardIds.ActivityMeetingStatus),
      meetingLocation: this.getStringFieldData(data, StandardIds.ActivityMeetingLocation),
      date: this.fieldService.getFieldValueFromCrmObject(data, SystemFieldIds.ActivityEffectiveDate)?.dateValue ?? null,
      meetingDuration: convertMillisecondsToHoursAndMinutes(duration),
      duration: duration,
      meetingSummaryTitle: this.getStringFieldData(data, StandardIds.ActivityMeetingSummaryTitle),
      meetingKeyTakeAways: this.getStringValuesFieldData(data, StandardIds.ActivityMeetingKeyTakeaways),
      meetingActionItems: this.getStringValuesFieldData(data, StandardIds.ActivityMeetingActionItems),
      meetingSummaryBody: this.getStringFieldData(data, StandardIds.ActivityMeetingSummaryBody),
      meetingSummarySentiment: this.getIntegerFieldData(data, StandardIds.ActivityMeetingSentiment),
      meetingSalesScore: this.getIntegerFieldData(data, StandardIds.ActivityMeetingSalesScore),
      meetingTranscriptUrl: this.getStringFieldData(data, StandardIds.ActivityMeetingTranscriptUrl),
      meetingRecordingUrl: this.getStringFieldData(data, StandardIds.ActivityMeetingRecordingUrl),
      processedTranscriptID: this.getStringFieldData(data, StandardIds.ActivityMeetingAnalysisProcessedTranscriptID),
      ownerId: this.getStringFieldData(data, SystemFieldIds.ActivityOwnerID),
      activity: data,
    };
  }

  headerText$ = computed(() => {
    const cardInfo = this.cardInfo;
    if (!cardInfo || !cardInfo.meetingStatus) {
      return this.translateService.stream('ACTIVITY.MEETING.STATUS_HEADER.UNKNOWN_STATUS');
    }

    const statusConfigs: { [key: string]: { i18nKey: string; supportsOwnerSuffix: boolean } } = {
      [MeetingStatus.Scheduled.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.STATUS_HEADER.SCHEDULED',
        supportsOwnerSuffix: true,
      },
      [MeetingStatus.Rescheduled.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.STATUS_HEADER.RESCHEDULED',
        supportsOwnerSuffix: true,
      },
      [MeetingStatus.Cancelled.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.STATUS_HEADER.CANCELLED',
        supportsOwnerSuffix: true,
      },
      [MeetingStatus.Completed.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.STATUS_HEADER.COMPLETED',
        supportsOwnerSuffix: true,
      },
      [MeetingStatus.NoShow.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.STATUS_HEADER.NO_SHOW',
        supportsOwnerSuffix: false,
      },
      [MeetingStatus.ReminderSent.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.REMINDER_SENT',
        supportsOwnerSuffix: false,
      },
      [MeetingStatus.ReminderDelivered.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.REMINDER_DELIVERED',
        supportsOwnerSuffix: false,
      },
      [MeetingStatus.ReminderFailed.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.REMINDER_FAILED',
        supportsOwnerSuffix: false,
      },
      [MeetingStatus.ConfirmationSent.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.CONFIRMATION_SENT',
        supportsOwnerSuffix: false,
      },
      [MeetingStatus.ConfirmationDelivered.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.CONFIRMATION_DELIVERED',
        supportsOwnerSuffix: false,
      },
      [MeetingStatus.ConfirmationFailed.toLowerCase()]: {
        i18nKey: 'ACTIVITY.MEETING.CONFIRMATION_FAILED',
        supportsOwnerSuffix: false,
      },
    };
    const statusConfig = statusConfigs[cardInfo.meetingStatus.toLowerCase()];
    if (!statusConfig) {
      return this.translateService.stream('ACTIVITY.MEETING.STATUS_HEADER.UNKNOWN_STATUS');
    }
    if (!statusConfig.supportsOwnerSuffix || !cardInfo.ownerId) {
      return this.translateService.stream(statusConfig.i18nKey);
    }

    // Otherwise we want to add the owner suffix and need to look up the user
    return this.userService.getMultiUsers([cardInfo.ownerId]).pipe(
      map((u) => {
        let statusText = this.translateService.instant(statusConfig.i18nKey);
        if (u && u[0]?.displayName) {
          statusText +=
            ' ' +
            this.translateService.instant('ACTIVITY.MEETING.STATUS_HEADER.BY_OWNER', { ownerName: u[0].displayName });
        }
        return statusText;
      }),
    );
  });

  cardTitle = computed(() => {
    const { duration } = this.cardInfo;
    const minutes = Math.floor((duration ?? 0) / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}-hour ${minutes % 60}-minute meeting`;
    }
    return `${minutes}-minute meeting`;
  });

  private readonly _aiIconService = inject(GalaxyAiIconService);

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
    private readonly sanitizer: DomSanitizer,
    private readonly dialog: MatDialog,
    private readonly associationService: ActivityAssociationService,
    private readonly userService: ActivityAssigneeService,
  ) {}

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getStringValuesFieldData(activity: ActivityInterface, fieldId: string): string[] {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValues?.values ?? [];
  }

  private getIntegerFieldData(activity: ActivityInterface, fieldId: string): number {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.integerValue ?? 0;
  }

  openSummaryModal(): void {
    const cardDetails = this.cardInfo;
    const companyList = this.companies();

    const dialogRef = this.dialog.open(MeetingSummaryModalComponent, {
      width: '950px',
      data: {
        meetingTitle: cardDetails.meetingSummaryTitle,
        meetingDate: cardDetails.date,
        duration: cardDetails.meetingDuration,
        sentimentScore: `${cardDetails.meetingSummarySentiment} / 10`,
        salesScore: `${cardDetails.meetingSalesScore} / 10`,
        summary: cardDetails.meetingSummaryBody,
        keyTakeaways: cardDetails.meetingKeyTakeAways,
        actionItems: cardDetails.meetingActionItems,
        transcriptUrl: cardDetails.meetingTranscriptUrl,
        recordingUrl: cardDetails.meetingRecordingUrl,
        companies: companyList,
        processedTranscriptID: cardDetails.processedTranscriptID,
        ownerID: cardDetails.ownerId,
        activity: cardDetails.activity,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isClosed = true;
    });
  }
}
